import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PictureImg from '@/components/PictureImg';

export default function About() {
    useDocumentMeta({
        title: 'About · Maison Touré',
        description: 'Maison Touré is a Sydney gallery for contemporary African art, founded by Fatym Touré in 2026 — provenance-led, privately viewed.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const a = settings?.about || {};
    const principles = a.principles || [];

    return (
        <div ref={reveal} data-testid="about-page" style={{ paddingTop: 144 }}>
            {/* HERO HEADLINE */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12">
                <div className="reveal max-w-5xl">
                    <span className="overline" style={{ color: 'var(--ink-2)' }}>{a.overline || 'About'}</span>
                    <h1
                        className="mt-10"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontWeight: 400,
                            fontSize: 'clamp(46px, 6.9vw, 120px)',
                            lineHeight: 0.96,
                            letterSpacing: '-0.028em',
                        }}
                    >
                        {a.headline_part1 || 'A house for the'} <em className="serif-italic" style={{ fontWeight: 500 }}>{a.headline_italic || 'slow looking'}</em> {a.headline_part2 || 'of contemporary African art.'}
                    </h1>
                </div>
            </section>

            {/* FATYM */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
                <div className="md:col-span-6 reveal">
                    <div className="img-wrap aspect-[4/5]" style={{ background: 'var(--bg-3)' }}>
                        {a.founder_image && <PictureImg src={a.founder_image} alt="Fatym Touré" />}
                    </div>
                </div>
                <div className="md:col-span-5 md:col-start-8 reveal">
                    <span className="overline" style={{ color: 'var(--ink-2)' }}>{a.founder_overline || 'Founder & Curator'}</span>
                    <h2
                        className="mt-6"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontWeight: 400,
                            fontSize: 'clamp(40px, 5vw, 76px)',
                            lineHeight: 1.02,
                            letterSpacing: '-0.022em',
                        }}
                    >
                        <em className="serif-italic" style={{ fontWeight: 500 }}>{a.founder_name || 'Fatym Touré.'}</em>
                    </h2>
                    <p
                        className="mt-10"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontWeight: 400,
                            fontSize: 19,
                            lineHeight: 1.75,
                            color: 'var(--ink-2)',
                        }}
                    >
                        {a.founder_body_1 || ''}
                    </p>
                    {a.founder_body_2 && (
                        <p
                            className="mt-6"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 19,
                                lineHeight: 1.75,
                                color: 'var(--ink-2)',
                            }}
                        >
                            {a.founder_body_2}
                        </p>
                    )}
                </div>
            </section>

            {/* PRINCIPLES */}
            <section className="rule-top">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-3">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{a.principles_overline || 'Our Principles'}</span>
                    </div>
                    <div className="md:col-span-8 md:col-start-5 flex flex-col">
                        {principles.map((p) => (
                            <div key={p.n} className="rule-bottom py-10 md:py-12 reveal">
                                <span
                                    className="serif-italic"
                                    style={{ fontSize: 22, color: 'var(--ink-3)' }}
                                >
                                    {p.n}
                                </span>
                                <h3
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontWeight: 500,
                                        fontSize: 'clamp(28px, 3vw, 44px)',
                                        lineHeight: 1.1,
                                        letterSpacing: '-0.015em',
                                    }}
                                >
                                    {p.title}.
                                </h3>
                                <p
                                    className="mt-5 max-w-xl"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 18,
                                        lineHeight: 1.7,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {p.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
